/**
 * ARTHAUS session token.
 *
 * Fetches a short-lived token from the concierge on first use and returns it as a header
 * on paid calls (search, recommend, space). The service uses it to decide HOW MUCH
 * reasoning to buy for a query — it is not authentication and it does not gate access.
 *
 * DEGRADES SILENTLY, ALWAYS. Every failure path returns {} rather than throwing or
 * blocking: a token fetch that fails, a browser with sessionStorage disabled, a service
 * that has not deployed the endpoint yet. The worst outcome is a cheaper answer, never a
 * broken search — which is the whole reason this is a header hint rather than a gate.
 *
 * ES5 on purpose: matches search.js / search-page.js and the theme's browser floor.
 */
(function (w) {
  'use strict';

  var KEY = 'arthaus_session_v1';
  var pending = null;

  function now() { return Math.floor(Date.now() / 1000); }

  function read() {
    try {
      var raw = w.sessionStorage.getItem(KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      // Refresh a minute early so a request never races its own expiry.
      if (!v || !v.token || !v.exp || v.exp - 60 <= now()) return null;
      return v.token;
    } catch (e) { return null; }
  }

  function write(token, ttl) {
    try {
      w.sessionStorage.setItem(KEY, JSON.stringify({ token: token, exp: now() + (ttl || 3600) }));
    } catch (e) { /* private mode, quota, disabled — the token just won't persist */ }
  }

  /** Resolve to a token, or null. Never rejects. */
  function fetchToken(apiBase) {
    var cached = read();
    if (cached) return Promise.resolve(cached);
    // One in-flight request at a time: a page with a search box and a facets sidebar would
    // otherwise mint several tokens on load and throw all but one away.
    if (pending) return pending;

    pending = fetch(String(apiBase || '').replace(/\/$/, '') + '/api/session', {
      method: 'GET',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        pending = null;
        if (!d || !d.token) return null;
        write(d.token, d.expires_in);
        return d.token;
      })
      .catch(function () { pending = null; return null; });

    return pending;
  }

  /** Header object to spread into a fetch. {} when there is no token — by design. */
  function headers(apiBase) {
    return fetchToken(apiBase).then(function (t) {
      return t ? { 'X-Arthaus-Session': t } : {};
    });
  }

  /** Called on page load so the first search does not pay for the round trip. */
  function warm(apiBase) { if (apiBase) fetchToken(apiBase); }

  w.ArthausSession = { headers: headers, warm: warm, token: fetchToken };
})(window);
